// The app's pages, in one place: the sidebar, the phone tab bar, the command
// palette and the top bar's breadcrumb all read this list, so a page added
// here shows up in each of them, and a page's roles are written once.
//
// Each entry keeps the shape { to: "/x", ..., roles: [...] } on one object:
// backend/tests/test_decision_authority.py reads it to check that every
// super_admin-only entry has a SuperAdminRoute in App.js.
import {
  LayoutDashboard,
  FileSearch,
  PlusCircle,
  Users,
  Shield,
  ShieldAlert,
  Sliders,
  ClipboardList,
  FileBarChart,
  ClipboardCheck,
  FolderOpen,
  DollarSign,
  FileText,
  ShieldCheck,
  ListChecks,
  FileBarChart2,
  BookCheck,
  Search,
  GraduationCap,
  Settings,
} from "lucide-react";

const ALL = ["admin", "super_admin", "user"];
const ADMINS = ["admin", "super_admin"];

// `helpKey` is the one-line description shown beside the page in the
// command palette and in the sidebar's hover card. `keywords` are extra
// words the palette matches on (both languages).
export const NAV_SECTIONS = [
  {
    id: "screening",
    labelKey: "nav.sections.screening",
    items: [
      { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, roles: ALL, helpKey: "help.tooltips.dashboard", keywords: "home accueil overview" },
      { to: "/transactions", labelKey: "nav.screenings", icon: FileSearch, roles: ALL, helpKey: "help.tooltips.screenings", keywords: "register list registre transactions" },
      { to: "/transactions/new", labelKey: "nav.newScreening", icon: PlusCircle, roles: ALL, helpKey: "help.tooltips.newScreening", keywords: "create add nouveau créer" },
      { to: "/projects", labelKey: "nav.projects", icon: FolderOpen, roles: ALL, helpKey: "help.tooltips.projects", keywords: "development développement vefa" },
    ],
  },
  {
    id: "clients",
    labelKey: "nav.sections.clients",
    items: [
      { to: "/client-register", labelKey: "nav.clientRegister", icon: Users, roles: ALL, helpKey: "help.tooltips.clientRegister", keywords: "parties buyers sellers acheteurs vendeurs" },
      { to: "/document-tracking", labelKey: "nav.documentTracking", icon: ClipboardList, roles: ALL, helpKey: "help.tooltips.documentTracking", keywords: "requests upload demandes" },
      { to: "/batch-review", labelKey: "nav.batchReview", icon: ClipboardCheck, roles: ALL, helpKey: "help.tooltips.batchReview", keywords: "bulk lot" },
    ],
  },
  {
    id: "pep",
    labelKey: "nav.sections.pep",
    items: [
      { to: "/pep-register", labelKey: "nav.pepRegister", icon: Shield, roles: ADMINS, helpKey: "help.tooltips.pepRegister", keywords: "ppe politically exposed" },
      { to: "/pep-review", labelKey: "nav.pepReviewQueue", icon: ShieldAlert, roles: ADMINS, helpKey: "help.tooltips.pepReviewQueue", keywords: "ppe queue file" },
      { to: "/pep-lifecycle-settings", labelKey: "nav.pepLifecycleSettings", icon: Sliders, roles: ["super_admin"], helpKey: "help.tooltips.pepLifecycleSettings", keywords: "ppe former ancien settings paramètres" },
    ],
  },
  {
    id: "reporting",
    labelKey: "nav.sections.reporting",
    items: [
      { to: "/mlro-dashboard", labelKey: "nav.mlroDashboard", icon: Shield, roles: ADMINS, helpKey: "help.tooltips.mlroDashboard", keywords: "mlro" },
      { to: "/str-reports", labelKey: "nav.strReports", icon: FileText, roles: ADMINS, helpKey: "help.tooltips.strReports", keywords: "str sar fiu suspicious suspect déclaration" },
      { to: "/report-settings", labelKey: "nav.complianceReports", icon: FileBarChart, roles: ALL, helpKey: "help.tooltips.complianceReports", keywords: "schedule email planifié" },
      { to: "/regulatory-reports", labelKey: "nav.regulatoryReports", icon: FileBarChart2, roles: ADMINS, helpKey: "help.tooltips.regulatoryReports", keywords: "regulator régulateur" },
    ],
  },
  {
    id: "audit",
    labelKey: "nav.sections.audit",
    items: [
      { to: "/audit-readiness", labelKey: "nav.auditReadiness", icon: ShieldCheck, roles: ADMINS, helpKey: "help.tooltips.auditReadiness", keywords: "gaps lacunes" },
      { to: "/compliance-checklist", labelKey: "nav.complianceChecklist", icon: ListChecks, roles: ADMINS, helpKey: "help.tooltips.complianceChecklist", keywords: "checklist liste" },
      { to: "/policy-attestation", labelKey: "nav.policyAttestation", icon: BookCheck, roles: ADMINS, helpKey: "help.tooltips.policyAttestation", keywords: "policies politiques" },
      { to: "/audit-log-search", labelKey: "nav.auditLogSearch", icon: Search, roles: ADMINS, helpKey: "help.tooltips.auditLogSearch", keywords: "trail journal history historique" },
      { to: "/training-log", labelKey: "nav.trainingLog", icon: GraduationCap, roles: ADMINS, helpKey: "help.tooltips.trainingLog", keywords: "training formation" },
    ],
  },
  {
    id: "admin",
    labelKey: "nav.sections.admin",
    items: [
      { to: "/admin", labelKey: "nav.adminPanel", icon: Settings, roles: ADMINS, helpKey: "help.tooltips.adminPanel", keywords: "users groups utilisateurs groupes webhooks" },
      { to: "/billing", labelKey: "nav.billing", icon: DollarSign, roles: ["super_admin"], helpKey: "help.tooltips.billing", keywords: "invoices factures" },
    ],
  },
];

const roleOf = (user) => user?.role || "user";

/** The sections a user sees, each holding only the pages their role opens. */
export function navSectionsFor(user) {
  const role = roleOf(user);
  return NAV_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => i.roles.includes(role)) }))
    .filter((s) => s.items.length > 0);
}

/** Every page a user may open, flat, in menu order. */
export function navItemsFor(user) {
  return navSectionsFor(user).flatMap((s) => s.items.map((i) => ({ ...i, sectionId: s.id, sectionLabelKey: s.labelKey })));
}

/**
 * The menu entry a path belongs to: the longest `to` that is the path or a
 * parent of it, so /transactions/abc is under Screenings while
 * /transactions/new stays New Screening.
 */
export function activeNavItem(pathname, items) {
  let best = null;
  for (const item of items) {
    const match = pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (match && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}

// The phone's bottom bar: four pages and "More", which opens the full menu.
export const MOBILE_TABS = ["/dashboard", "/transactions", "/transactions/new", "/document-tracking"];

export function mobileTabsFor(user) {
  const items = navItemsFor(user);
  return MOBILE_TABS.map((to) => items.find((i) => i.to === to)).filter(Boolean);
}

/**
 * Badge counts by page, from /api/notifications/summary: each item carries
 * the link of the page that resolves it.
 */
export function badgeCounts(summaryItems) {
  const counts = {};
  for (const item of summaryItems || []) {
    if (!item?.link || !(item.count > 0)) continue;
    counts[item.link] = (counts[item.link] || 0) + item.count;
  }
  return counts;
}

/** A badge's text: the count, or 99+ past 99. */
export function badgeText(n) {
  return n > 99 ? "99+" : String(n);
}

// Keyboard shortcuts that open a page: "g" then the key.
export const GO_SHORTCUTS = {
  d: "/dashboard",
  s: "/transactions",
  p: "/projects",
  c: "/client-register",
  t: "/document-tracking",
  r: "/report-settings",
  m: "/mlro-dashboard",
  a: "/admin",
};
